import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alignment, ButtonTypes, ChatInputButton } from 'pages/chat/chat';
import { FileUploadPopover } from './fileUploaderPopover';
import { MAX_FILE_SIZE } from 'constant/file';
import { ChatPlusMenuExtras } from './chat-plus-menu-extras';
import { X } from 'lucide-react';
import { ArrowUp, Plus } from 'iconoir-react';
import { uploadFilesToVectorStorage } from '../../../services/storage-service';
import {
  useCompletePreSignedUpload,
  useGetLibraryPreSignedUrlForUpload,
} from 'lib/api/hooks/use-storage';
import {
  Attachment,
  ButtonIds,
  MessageRole,
  VectorUploadItem,
  Mention,
  type MessageSkillMention,
} from '../types/chat';
import type { IntegrationSource } from 'services/sse-service';
import { v4 as uuidv4 } from 'uuid';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { Button } from 'components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import { allSupportedImageExtensions, modelSupportedExtensionsMap, ModelTypes, VECTORIZE_EXCLUDE_EXTENSIONS, type IModel } from '../types/model.types';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import CustomToast from 'components/core/CustomToast';
import { useNotificationListener } from 'hooks/use-notification-listener';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useTheme } from '../../../components/core/theme-provider';
// import { useNotification } from 'hooks/use-notification';
// import { useCollaborationContext } from '../../../state/collaboration-websocket-middleware';
import { DictateButton } from './DictateButton';
import { VoiceCallButton } from './VoiceCallButton';
import { TypingIndicator } from './TypingIndicator';
import { formatDate } from '../../../utils/custom-date';
import { parseMessageTimeForDisplay } from '../utils/message-show-time';
import { ActiveToolsDisplay, ToolsSelector } from './ToolsSelector';
import { IamData } from 'features/iam/services/user-service';
import {
  adjustMentionsForMarkdown,
} from '../../../utils/chat-utilities';
import { MentionsInput, Mention as ReactMention, type OnChangeHandlerFunc, type SuggestionDataItem } from 'react-mentions';
import { useCollaborationSocketStore } from 'state/store/collaboration/use-collaboration-store';
import { useCommonStore } from '../../../state/store/common/useCommonStore';
import seliseAILogo from '../../../assets/images/selise_ai_small.png';
import { StopGeneratingMessage } from './StopGeneratingMessage';
import { SourceDocumentSelector } from './SourceDocumentSelector';
import { ModelSelector } from '../../../components/blocks/model-selector';
import { AgentSelector } from '../../../components/blocks/agent-selector';
import { ProjectSelector } from 'features/projects/components/project-selector';
import { UsageLimitBanner } from 'features/credits/components/usage-limit-banner';
import { UsageLimitCircle } from 'features/credits/components/usage-limit-circle';
import { ChatInputStepHeader } from './chat-input-step-header';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { ApplyPromptVariablesModal } from '../agent-chat-module/components/gpt-chat-input/apply-prompt-variables-modal';
import { NewProjectModal } from 'features/projects/components/new-project-modal';
import {
  ContextMentionPicker,
  type MentionPickerView,
} from '../agent-chat-module/components/gpt-chat-input/context-mention-picker';
import { useChatMentionSearch } from '../agent-chat-module/hooks/use-chat-mention-search';
import { AgentMentionPicker } from '../agent-chat-module/components/gpt-chat-input/agent-mention-picker';
import { useAgentMention } from '../hooks/use-agent-mention';
import type { MentionSearchType } from '../agent-chat-module/types/mentions.service.type';
import type { MentionPickerItem } from '../utils/normalize-mention-search';
import {
  buildMessageWithPrompt,
  getCursorAfterPromptInsert,
  type PromptInsertContext,
} from '../utils/insert-prompt-into-message';
import {
  getPromptMentionState,
  CONTEXT_MENTION_TRIGGER,
  resolveContextTriggerPlaceholder,
  type MentionState,
} from '../utils/prompt-mention';
import type { PromptItem } from 'pages/prompts/types';
import { extractVariables } from 'pages/prompts/utils';
import { buildPromptInsertContext } from '../utils/prompt-mention-anchor';
import {
  appendSkillToSelection,
  clearMentionTrigger,
  toMessageSkillMentions,
} from '../utils/mention-trigger';
import { SelectedSkillChips } from '../agent-chat-module/components/gpt-chat-input/selected-skill-chips';
import {
  buildGroupChatMentionMarkup,
  collapseDuplicateAtSigns,
  hasGroupChatMentionMarkup,
} from '../utils/mention-markup';
import { getActiveIntegrationConnections, getIntegrationSelections } from 'services/integration-service';
import { ChatQuickActions } from './ChatQuickActions';
import { ExploreIdeasSection, type ImageIdea } from './ExploreIdeasSection';
import { WriteOrEditPromptList, type WriteOrEditPrompt } from './WriteOrEditPromptList';
import { AgentsSection } from './AgentsSection';
import { AttachmentFileCardList } from './attachment-file-card';

const SHAREPOINT_SOURCE_IDS = new Set(['sharepoint', 'microsoft-sharepoint']);

// Typing indicator component with Microsoft Teams-like animation

const MAX_PROCESSING_TIME = 5 * 60 * 1000; // 5 minutes
const FILE_UPLOAD_NO_EXTRACTABLE_TEXT_MESSAGE =
  'No readable text was found in this file. Scanned or image-only documents are not currently supported.';

interface ChatInputProps {
  /** The conversation's agent no longer resolves for this user — block sends
   *  (retryable: a successful agent refetch clears it). */
  agentUnavailable?: boolean;
  onSendMessage: (
    content: string,
    files: Attachment[],
    sources?: IntegrationSource[],
    mentions?: Mention[],
    selectedTools?: string[],
    skillMentions?: MessageSkillMention[]
  ) => void | Promise<void>;
  isLoading: boolean;
  inputButtons?: ChatInputButton[];
  isCurrentResponseGenerating: boolean;
  onHeightChange?: (height: number) => void;
  isFeatureSelected?: Record<ButtonIds, boolean>;
  hasParticipants?: boolean;
  selectedSources?: string[];
  onSourcesChange?: (sources: string[]) => void;
  selectedFolderIdsBySource?: Record<string, string[]>;
  onSelectedFolderIdsBySourceChange?: (next: Record<string, string[]>) => void;
  onToggleTool?: (toolId: ButtonIds) => void;
  onToggleSourcesTool?: () => void;
  isSourcesToolActive?: boolean;
  isCancelOnGoing?: boolean;
  canCancelGeneration?: boolean;
  isModelSelectionDisabled?: boolean;
  selectedTools?: string[];
  onSelectedToolsChange?: (tools: string[]) => void;
  showQuickActions?: boolean;
  /** Agent mode: selected agent (locks the model display), change handler gets the
   * current draft so a mid-chat switch can carry it into the fresh chat. */
  selectedAgentId?: string | null;
  onAgentChange?: (agentId: string | null, draftText: string) => void;
  showAgentSelector?: boolean;
  /** Show the agent selector in a tab above the input, next to the project selector,
   * instead of in the composer row. */
  showAgentProjectTab?: boolean;
  /** Show the "+" menu (upload, skills, tools) in the composer footer. */
  showMoreOptions?: boolean;
  agentModelLabel?: string | null;
  agentModelLoading?: boolean;
  agentModelRegion?: IModel['region'];
  agentUploadModel?: IModel | null;
  initialDraft?: string;
  /** Create-agent chat hides the quick-action chips under the composer. */
  isCreateAgentMode?: boolean;
  /** Start a voice call with the conversation's agent; the button is hidden when omitted. */
  onStartVoiceCall?: () => void;
  isVoiceCallActive?: boolean;
  /** Identifies the chat on screen; a change cancels an in-progress dictation. */
  chatTargetKey?: string;
}

/** Survives ChatInput remounts so a learned-skill notification delivered twice cannot re-toast. */
const handledLearnedSkillIds = new Set<string>();

export const ChatInput = ({
  agentUnavailable = false,
  onStartVoiceCall,
  isVoiceCallActive = false,
  chatTargetKey,
  onSendMessage,
  isLoading,
  inputButtons,
  isCurrentResponseGenerating,
  isFeatureSelected,
  onHeightChange,
  hasParticipants,
  selectedSources: externalSelectedSources,
  onSourcesChange: externalOnSourcesChange,
  selectedFolderIdsBySource: externalSelectedFolderIdsBySource,
  onSelectedFolderIdsBySourceChange: externalOnSelectedFolderIdsBySourceChange,
  onToggleTool,
  onToggleSourcesTool: externalOnToggleSourcesTool,
  isSourcesToolActive: externalIsSourcesToolActive,
  isCancelOnGoing,
  canCancelGeneration = false,
  isModelSelectionDisabled,
  selectedTools = [],
  onSelectedToolsChange,
  showQuickActions = false,
  selectedAgentId,
  onAgentChange,
  showAgentSelector = false,
  showAgentProjectTab = false,
  showMoreOptions = true,
  agentModelLabel,
  agentModelLoading,
  agentModelRegion,
  agentUploadModel,
  initialDraft,
  isCreateAgentMode = false,
}: ChatInputProps) => {
  const { t } = useTranslation();
  const isCreateImageActive = isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false;
  const isWriteOrEditActive = isFeatureSelected?.[ButtonIds.WRITE_OR_EDIT] ?? false;
  const isAgentsActive = isFeatureSelected?.[ButtonIds.AGENTS] ?? false;
  const hasQuickActionButtons =
    (inputButtons?.filter((button) => button.alignment === Alignment.LEFT).length ?? 0) > 0;
  const showExploreIdeas = showQuickActions && isCreateImageActive;
  const showWriteOrEditPrompts = showQuickActions && isWriteOrEditActive;
  const showAgents = showQuickActions && isAgentsActive;
  const showQuickActionButtons =
    showQuickActions &&
    !isCreateAgentMode &&
    !isCreateImageActive &&
    !isWriteOrEditActive &&
    !isAgentsActive &&
    hasQuickActionButtons;
  const showBelowInputSection =
    showQuickActionButtons || showExploreIdeas || showWriteOrEditPrompts || showAgents;
  const isInputFocusedRef = useRef<boolean>(false);
  const replyMessage = useChatStore((state) => state.replyMessage);
  const setReplyMessage = useChatStore((state) => state.setReplyMessage);
  // console.log('isCurrentResponseGenerating', isCurrentResponseGenerating)

  const [message, setMessage] = useState(initialDraft ?? '');
  const [messageMarkup, setMessageMarkup] = useState(initialDraft ?? '');
  const [promptMention, setPromptMention] = useState<MentionState>({
    active: false,
    query: '',
    startIndex: -1,
  });
  const [promptMentionCursor, setPromptMentionCursor] = useState(0);
  const [mentionPickerView, setMentionPickerView] = useState<MentionPickerView>('categories');
  const [mentionCategory, setMentionCategory] = useState<MentionSearchType | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<MentionPickerItem[]>([]);
  const [applyPromptState, setApplyPromptState] = useState<{
    prompt: PromptItem;
    insertContext: PromptInsertContext;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const expandedQuickPanelRef = useRef<HTMLDivElement>(null);
  const mentionInsertRef = useRef<PromptInsertContext | null>(null);
  const presignedUploadMutation = useGetLibraryPreSignedUrlForUpload();
  const completeUploadMutation = useCompletePreSignedUpload();

  const uploadFileViaPresignedUrl = useCallback(
    async (file: File, fileId: string): Promise<{ isSuccess: boolean }> => {
      try {
        const presigned = await presignedUploadMutation.mutateAsync({
          itemId: fileId,
          name: file.name,
          projectKey: configurationSettings.blocksKey,
          accessModifier: 'Private',
          metaData: '',
          parentDirectoryId: '',
          tags: '',
        });
        if (!presigned.isSuccess || !presigned.uploadUrl) {
          return { isSuccess: false };
        }
        const completedFileId = presigned.fileId || fileId;
        const blobResp = await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type, 'x-ms-blob-type': 'BlockBlob' },
        });
        if (blobResp.ok && completedFileId) {
          try {
            await completeUploadMutation.mutateAsync([completedFileId]);
          } catch (recordErr) {
            console.error('Recording the upload failed:', recordErr);
          }
        }
        return { isSuccess: blobResp.ok };
      } catch (err) {
        console.error('Presigned upload failed:', err);
        return { isSuccess: false };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presignedUploadMutation, completeUploadMutation],
  );
  const mentionsInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [onProcessing, setOnProcessing] = useState<Attachment[]>([]);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);
  const [emptyFiles, setEmptyFiles] = useState<string[]>([]);
  const selectedModel = useModelStore((state) => state.selectedModel);
  // Upload/extension rules follow the agent's model in agent mode; if the agent's
  // model isn't in the LLM list, stay permissive — the BE enforces its own limits.
  const uploadModel = useMemo<IModel | null>(() => {
    if (selectedModel) return selectedModel;
    if (!selectedAgentId) return selectedModel;
    return (
      agentUploadModel ??
      ({ id: 'auto', name: agentModelLabel ?? 'auto', modelType: ModelTypes.VISION } as IModel)
    );
  }, [selectedModel, selectedAgentId, agentUploadModel, agentModelLabel]);
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const removeConversationFromSidebarById = useChatStore(
    (state) => state.removeConversationFromSidebarById
  );
  const isNewChat = useChatStore((state) => state.isNewChat);
  const setNewChat = useChatStore((state) => state.setNewChat);
  const [isSendDisabled, setIsSendDisabled] = useState(true);
  const itemIdFilePathMap = useChatStore((state) => state.itemIdFilePathMap);
  const setItemIdFilePathMap = useChatStore((state) => state.setItemIdFilePathMap);
  const clearItemIdFilePathMap = useChatStore((state) => state.clearItemIdFilePathMap);
  const { toast } = useToast();
  const { configurationSettings } = useConfig();
  const sendMessage = useCollaborationSocketStore((state: any) => state.sendMessage);
  const [processingToastOpen, setProcessingToastOpen] = useState(false);
  const [processingToastTitle, setProcessingToastTitle] = useState('');
  const [processingToastDescription, setProcessingToastDescription] = useState('');
  const [fileUploadingToastOpen, setFileUploadingToastOpen] = useState(false);
  const [fileUploadingToastTitle, setFileUploadingToastTitle] = useState('');
  const [fileUploadingToastDescription, setFileUploadingToastDescription] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sharePointSourcesCache, setSharePointSourcesCache] = useState<IntegrationSource[]>([]);
  const [isLoadingSharePointSources, setIsLoadingSharePointSources] = useState(false);

  const [isDictationOnGoing, setIsDictationOnGoing] = useState(false);
  const sendTypingStatus = (conversationId: string, isTyping: boolean) => {
    sendMessage({
      type: 'TYPING_STATUS',
      request: { conversationId, is_typing: isTyping },
    });
  }

  const [timeouts, setTimeouts] = useState<Map<string, ReturnType<typeof setTimeout>>>(new Map());


  // Use external state if provided, otherwise use internal state
  const [internalSelectedSources, setInternalSelectedSources] = useState<string[]>([]);
  const selectedSources = externalSelectedSources ?? internalSelectedSources;
  const setSelectedSources = externalOnSourcesChange ?? setInternalSelectedSources;
  const [internalSelectedFolderIdsBySource, setInternalSelectedFolderIdsBySource] = useState<Record<string, string[]>>({});
  const selectedFolderIdsBySource = externalSelectedFolderIdsBySource ?? internalSelectedFolderIdsBySource;
  const setSelectedFolderIdsBySource = externalOnSelectedFolderIdsBySourceChange ?? setInternalSelectedFolderIdsBySource;
  // State to control SourcesSelector popover from ToolsSelector
  const [sourcesPopoverOpen, setSourcesPopoverOpen] = useState(false);
  // State for mobile menu popover
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);

  // State to track if sources tool is active - use external if provided
  const [internalIsSourcesToolActive, setInternalIsSourcesToolActive] = useState(false);
  const isSourcesToolActive = externalIsSourcesToolActive ?? internalIsSourcesToolActive;

  const hasParticipantsArray = useChatStore((state) => state.hasParticipantsArray);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const theme = useTheme();
  const conversations = useChatStore((state) => state.conversations);
  const [isDragging, setIsDragging] = useState(false);

  const isGroupChat = !!hasParticipants && !!hasParticipantsArray && hasParticipantsArray.length > 1;
  const { agentMention, updateAgentMention, closeAgentMention, selectAgentMention } = useAgentMention({
    enabled: !isGroupChat && showAgentSelector && !!onAgentChange,
    onAgentChange,
    onMessageChange: (nextMessage) => {
      setMessage(nextMessage);
      setMessageMarkup(nextMessage);
    },
  });
  // Trust the parent for stop/send. Do not infer from the global active conversation —
  // landing composers (project / custom GPT / scheduler) pass false while a background
  // chat may still be streaming, and a stale isThisGenerationOnGoing would stick Stop forever.
  const isSendBlocked =
    agentUnavailable || (!isGroupChat && isCurrentResponseGenerating);

  // Dynamic placeholders with rotation
  const mentionTypesFilter =
    promptMention.active && mentionCategory && !promptMention.query.trim()
      ? [mentionCategory]
      : undefined;

  const {
    data: promptMentionSearchResult,
    isLoading: isPromptMentionLoading,
    isCategoriesLoading: isPromptMentionCategoriesLoading,
  } = useChatMentionSearch(
    promptMention.query,
    !isGroupChat && promptMention.active,
    mentionTypesFilter,
    selectedAgentId ? { surface: 'agents_v2' } : undefined
  );
  const promptMentionResults = promptMentionSearchResult?.items ?? [];
  const promptMentionCategories = promptMentionSearchResult?.categories ?? [];

  useEffect(() => {
    if (!promptMention.active) {
      setMentionPickerView('categories');
      setMentionCategory(null);
      return;
    }
    mentionInsertRef.current = buildPromptInsertContext(
      message,
      promptMention,
      promptMentionCursor
    );
  }, [promptMention.active, promptMention.startIndex, promptMention.query, message, promptMentionCursor]);

  const placeholders = useMemo(
    () => [
      // Prompts hint hidden while the `/` menu has no Prompts category. Restore with the
      // Prompts category (MENTION_SEARCH_TYPES_PARAM); the skills hint is index 0 until then.
      // resolveContextTriggerPlaceholder(
      //   t('CHAT_INPUT_PLACEHOLDER_ASK_ANYTHING', { defaultValue: 'Ask anything, / for prompts' }),
      //   'CHAT_INPUT_PLACEHOLDER_ASK_ANYTHING',
      //   'Ask anything, / for prompts',
      // ),
      resolveContextTriggerPlaceholder(
        t('CHAT_INPUT_PLACEHOLDER_ASK_ANYTHING_SKILLS', {
          defaultValue: 'Ask anything, / for skills',
        }),
        'CHAT_INPUT_PLACEHOLDER_ASK_ANYTHING_SKILLS',
        'Ask anything, / for skills',
      ),
      t('CHAT_INPUT_PLACEHOLDER', { defaultValue: 'Ask anything...' }),
      t('CHAT_INPUT_PLACEHOLDER_BRAINSTORM', { defaultValue: 'Help me brainstorm...' }),
      t('CHAT_INPUT_PLACEHOLDER_EXPLAIN', { defaultValue: 'Explain this to me...' }),
      t('CHAT_INPUT_PLACEHOLDER_HELP_WITH', { defaultValue: 'What can I help with...' }),
      t('CHAT_INPUT_PLACEHOLDER_SOLVE_TOGETHER', {
        defaultValue: "Let's solve this together...",
      }),
    ],
    [t]
  );

  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [placeholderOpacity, setPlaceholderOpacity] = useState(1);
  const activePlaceholder = placeholders[currentPlaceholderIndex];
  const showRotatingPlaceholder = !message.trim();

  // When input is cleared, show the primary / hint first again
  useEffect(() => {
    if (showRotatingPlaceholder) {
      setCurrentPlaceholderIndex(0);
    }
  }, [showRotatingPlaceholder]);

  // Rotate placeholders every 3 seconds with fade effect (empty input only)
  useEffect(() => {
    if (!showRotatingPlaceholder) {
      setPlaceholderOpacity(1);
      return;
    }

    const interval = setInterval(() => {
      setPlaceholderOpacity(0);

      setTimeout(() => {
        setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
        setPlaceholderOpacity(1);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, [placeholders.length, showRotatingPlaceholder]);

  const cachedUsers = useMemo(() => {
    const currentConv = activeConversationId ? conversations[activeConversationId] : null;
    const participants = currentConv?.participants ?? [];
    const removedParticipants = currentConv?.removedParticipants ?? [];
    const map = new Map<string, IamData>();
    [...participants, ...removedParticipants].forEach((u) => {
      if (u?.itemId && !map.has(u.itemId)) {
        map.set(u.itemId, u);
      }
    });
    return Array.from(map.values());
  }, [activeConversationId, conversations]);

  const mentionUsers = useMemo(() => {
    // Use cached users (active + removed) and add model as mentionable entity (SeliseAI)
    const currentUserId = useChatStore.getState().activeConversationId
      ? useCommonStore.getState().user?.id
      : useCommonStore.getState().user?.id;
    let users = cachedUsers.filter((u) => u.itemId !== currentUserId);

    if (selectedModel) {
      const modelAsUser: IamData = {
        itemId: selectedModel.id,
        createdDate: '',
        lastUpdatedDate: '',
        lastLoggedInTime: '',
        language: '',
        salutation: '',
        firstName: 'SeliseAI',
        lastName: null,
        email: selectedModel.name || selectedModel.id,
        userName: selectedModel.name || selectedModel.id,
        phoneNumber: null,
        roles: [],
        permissions: [],
        active: true,
        isVarified: false,
        profileImageUrl: null,
        mfaEnabled: false,
        organizations: [],
        plan: { itemId: '', name: '' },
        planItem: { itemId: '', title: null },
        groups: [],
      };
      users = [modelAsUser, ...users.filter((u) => u.itemId !== selectedModel.id)];
      // users = [...users.filter((u) => u.itemId !== selectedModel.id)];

    }

    // Dedup again (defensive)
    const map = new Map<string, IamData>();
    users.forEach((u) => {
      if (u?.itemId && !map.has(u.itemId)) map.set(u.itemId, u);
    });
    return Array.from(map.values());
  }, [cachedUsers, selectedModel]);



  const mentionData: SuggestionDataItem[] = useMemo(
    () =>
      mentionUsers.map((u) => ({
        id: u.itemId,
        display: `${u.firstName ?? ''}${u.lastName ? ` ${u.lastName}` : ''}`.trim() || u.email || u.itemId,
      })),
    [mentionUsers]
  );

  const mentionDataProvider = useCallback(
    (search: string, callback: (data: SuggestionDataItem[]) => void) => {
      if (!isGroupChat) {
        callback([]);
        return;
      }
      const q = (search || '').toLowerCase();
      if (!q) {
        callback(mentionData);
        return;
      }
      callback(
        mentionData.filter((item) => String(item.display ?? '').toLowerCase().includes(q))
      );
    },
    [isGroupChat, mentionData]
  );

  const mentionUsersMap = useMemo(() => {
    const map = new Map<string, IamData>();
    mentionUsers.forEach((u) => map.set(u.itemId, u));
    return map;
  }, [mentionUsers]);

  const mapReactMentionsToMentions = useCallback(
    (reactMentions: Array<{ id: string; display: string; plainTextIndex: number }>): Mention[] => {
      return reactMentions.map((m) => {
        const display = m.display ?? '';
        const start = m.plainTextIndex;
        const end = start + display.length + 1; // include '@'
        return {
          userId: m.id,
          trigger: `@${display}`,
          start,
          end,
          user: mentionUsersMap.get(m.id),
        };
      });
    },
    [mentionUsersMap]
  );


  useEffect(() => {
    if (isNewChat && !hasParticipants) {
      // A draft handed to this mount (onboarding starter, create-agent) is the new chat's text.
      // The landing can flag a new chat after mount, which must not wipe it.
      setMessage(initialDraft ?? '');
      setMessageMarkup(initialDraft ?? '');
      setFiles([]);
      setOnProcessing([]);
      setFailedFiles([]);
      setEmptyFiles([]);
      setIsSendDisabled(true);
      clearAllTimeouts();

      isInputFocusedRef.current = false;
      // setIsTyping(false);
      clearItemIdFilePathMap();
      setProcessingToastOpen(false);
      setNewChat(false);
      setFileUploadingToastOpen(false);
      setReplyMessage(null);
      setSelectedSources([]);
      if (externalIsSourcesToolActive === undefined) {
        setInternalIsSourcesToolActive(false);
      }
      setMentions([]);
    }
  }, [isNewChat, clearItemIdFilePathMap, setNewChat, setReplyMessage, externalIsSourcesToolActive, hasParticipants, activeConversationId, sendTypingStatus, initialDraft]);

  useEffect(() => {
    if (!initialDraft) return;
    setMessage(initialDraft);
    setMessageMarkup(initialDraft);
  }, [initialDraft]);

  const syncEditorHeight = useCallback(() => {
    const textarea = mentionsInputRef.current;
    if (!textarea) return;

    const control = textarea.closest('.chat-mentions-input__control');
    const highlighter = textarea
      .closest('.chat-mentions-input')
      ?.querySelector('.chat-mentions-input__highlighter');

    const minHeight = '48px';
    textarea.style.overflowY = 'hidden';
    textarea.style.height = minHeight;
    if (control instanceof HTMLElement) {
      control.style.height = minHeight;
    }
    if (highlighter instanceof HTMLElement) {
      highlighter.style.height = minHeight;
    }

    // Measure with overflow hidden so scrollHeight does not include an extra blank line.
    const contentHeight = textarea.scrollHeight;
    const newHeight = Math.max(48, Math.min(contentHeight, 160));
    const heightPx = `${newHeight}px`;
    // Only allow a scrollbar once the 160px cap is hit. Below that, a scrollbar steals width,
    // shifts the wrap point, and leaves the caret drawn against a differently wrapped highlighter.
    const overflowY = contentHeight > 160 ? 'auto' : 'hidden';
    textarea.style.height = heightPx;
    textarea.style.overflowY = overflowY;
    if (control instanceof HTMLElement) {
      control.style.height = heightPx;
    }
    if (highlighter instanceof HTMLElement) {
      highlighter.style.height = heightPx;
      highlighter.style.overflowY = overflowY;
    }

    const heightTarget = showBelowInputSection ? outerContainerRef.current : containerRef.current;
    if (heightTarget && onHeightChange) {
      onHeightChange(heightTarget.offsetHeight + 64);
    }
  }, [onHeightChange, showBelowInputSection]);

  const adjustEditorHeight = useCallback(() => {
    // Use double requestAnimationFrame to ensure DOM is fully rendered
    // This is critical for paste operations where content is added all at once
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncEditorHeight();
      });
    });
  }, [syncEditorHeight]);

  const addTimeout = (fileId: string, timeout: ReturnType<typeof setTimeout>) => {
    setTimeouts((prev) => new Map(prev.set(fileId, timeout)));
  };

  const clearTimeoutByFileId = (fileId: string) => {
    const timeout = timeouts.get(fileId);
    if (timeout) {
      clearTimeout(timeout);
      setTimeouts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(fileId);
        return newMap;
      });
    }
  };

  const clearAllTimeouts = () => {
    timeouts.forEach((timeout) => clearTimeout(timeout));
    setTimeouts(new Map());
  };

  // Typing status functions with debounce
  const handleTypingStop = useCallback(() => {
    if (!activeConversationId) return;

    if (isTyping) {
      sendTypingStatus(activeConversationId, false);
      setIsTyping(false);
    }
  }, [activeConversationId, sendTypingStatus]);


  // react-mentions handles its own dropdown; no outside-click handler needed


  useNotificationListener('blocks-notification', (event) => {
    const rawPayload = (event as { message?: { denormalizedPayload?: unknown } })?.message
      ?.denormalizedPayload;
    if (typeof rawPayload !== 'string') {
      return;
    }

    try {
      const payload = JSON.parse(rawPayload);
      const notificationType = payload['type'];

      if (notificationType === 'file_upload') {
        const success = payload['success'];
        const message = payload['message'];
        const fileId = payload['file_id'];
        clearTimeoutByFileId(fileId);
        setOnProcessing((prevFiles) => prevFiles.filter((file) => file.fileId !== fileId));

        const fileExists = onProcessing.some((file) => file.fileId === fileId);
        if (!fileExists) {
          return;
        }

        if (!success) {
          setFiles((prevFiles) => prevFiles.filter((file) => file.fileId !== fileId));
          if (message === 'empty_file') {
            setEmptyFiles((prevFiles) => [...prevFiles, fileId]);
          } else {
            setFailedFiles((prevFiles) => [...prevFiles, fileId]);
          }
        }

        if (onProcessing.length > 1) {
          return;
        }

        setProcessingToastOpen(false);

        if (emptyFiles.length > 0 || message === 'empty_file') {
          toast({
            variant: 'destructive',
            title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
            description: t('EMPTY_FILE_DESCRIPTION'),
          });
        } else if (message === 'file_upload_no_extractable_text') {
          toast({
            variant: 'destructive',
            title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
            description: t('FILE_UPLOAD_NO_EXTRACTABLE_TEXT', {
              defaultValue: FILE_UPLOAD_NO_EXTRACTABLE_TEXT_MESSAGE,
            }),
          });
        } else if (failedFiles.length > 0) {
          toast({
            variant: 'destructive',
            title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
            description: t('NOT_UPLOADED_VECTOR_STORAGE'),
          });
        } else if (success) {
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('UPLOADED_VECTOR_STORAGE_SUCCESS'),
          });
        } else {
          toast({
            variant: 'destructive',
            title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
            description: t('SOMETHING_WENT_WRONG'),
          });
        }
        setEmptyFiles([]);
        setFailedFiles([]);
      } else if (notificationType === 'learned_skill') {
        const skillId = String(payload.skill_id ?? '');
        if (!skillId || handledLearnedSkillIds.has(skillId)) {
          return;
        }
        handledLearnedSkillIds.add(skillId);

        const skillName = payload.display_name || payload.name;
        const isCurrentConversation =
          activeConversationId != null &&
          String(activeConversationId) === String(payload.conversation_id);

        if (
          isCurrentConversation &&
          (payload.action === 'created' || payload.action === 'updated')
        ) {
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: `${skillName} ${payload.action}`,
          });
        }
      }
    } catch (error) {
      console.error('Error parsing notification payload in ChatInput:', error);
    }
  });

  useEffect(() => {
    setFiles([]);
    setReplyMessage(null);
    //setSelectedSources([]);
  }, [activeConversationId, setReplyMessage]);

  // Focus the composer when a reply is set. Do not scroll the page — the composer stays pinned.
  useEffect(() => {
    if (!replyMessage) return undefined;
    const timer = setTimeout(() => {
      mentionsInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [replyMessage]);

  useEffect(() => {
    adjustEditorHeight();
  }, []);

  // Resize after programmatic content changes (e.g. @ prompt insert) once MentionsInput commits value
  useLayoutEffect(() => {
    syncEditorHeight();
  }, [messageMarkup, syncEditorHeight]);

  // Line wrapping also changes when the composer width changes (sidebar toggle, viewport resize)
  // or when the web font finishes loading, none of which touch messageMarkup. Without re-measuring,
  // the box keeps a stale height and clips the last line together with the caret.
  useEffect(() => {
    const control = mentionsInputRef.current?.closest('.chat-mentions-input__control');
    if (!(control instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return;

    let lastWidth = control.offsetWidth;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0;
      if (width === lastWidth) return;
      lastWidth = width;
      syncEditorHeight();
    });
    observer.observe(control);
    document.fonts?.ready.then(() => syncEditorHeight());

    return () => observer.disconnect();
  }, [syncEditorHeight, isDictationOnGoing]);

  useEffect(() => {
    const hasText = message.trim().length > 0;
    const hasSkills = selectedSkills.length > 0;
    const hasFiles = files.length > 0;
    setIsSendDisabled(!hasText && !hasSkills && !hasFiles);
  }, [message, selectedSkills.length, files.length]);

  // Clear files when sources are selected
  useEffect(() => {
    const isDocumentManagerActive = selectedSources.includes('document-manager');
    if (selectedSources.length > 0 &&
      files.length > 0 &&
      !isDocumentManagerActive) {
      setFiles([]);
      setOnProcessing([]);
    }
  }, [selectedSources.length, files.length, selectedSources]);


  // Also measure height when files change (affects container height)
  useEffect(() => {
    const heightTarget = showBelowInputSection ? outerContainerRef.current : containerRef.current;
    if (heightTarget && onHeightChange) {
      const height = heightTarget.offsetHeight;
      onHeightChange(height + 64);
    }
  }, [files.length, onHeightChange, inputButtons?.length, showBelowInputSection, isCreateImageActive, isWriteOrEditActive, isAgentsActive]);

  const buildBaseIntegrationSources = useCallback((): IntegrationSource[] => {
    return selectedSources
      .map((sourceId) => {
        if (sourceId === 'document-manager') {
          const folderIds = selectedFolderIdsBySource[sourceId] ?? [];
          return folderIds.length > 0
            ? { source: 'knowledge-base', folderIds }
            : null;
        }

        if (SHAREPOINT_SOURCE_IDS.has(sourceId)) {
          return null;
        }

        return { source: sourceId };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [selectedFolderIdsBySource, selectedSources]);

  useEffect(() => {
    const hasSharePointSelected = selectedSources.some((sourceId) => SHAREPOINT_SOURCE_IDS.has(sourceId));
    if (!hasSharePointSelected) {
      setSharePointSourcesCache([]);
      setIsLoadingSharePointSources(false);
      return;
    }

    if (!configurationSettings) {
      return;
    }

    let isCancelled = false;

    const loadSharePointSources = async () => {
      setIsLoadingSharePointSources(true);

      try {
        const activeConnections = await getActiveIntegrationConnections(configurationSettings);
        const sharePointConnections = activeConnections.filter((connection) =>
          SHAREPOINT_SOURCE_IDS.has(connection.provider_id)
        );

        if (sharePointConnections.length === 0) {
          if (!isCancelled) {
            setSharePointSourcesCache([]);
          }
          return;
        }

        const selectionResponses = await Promise.all(
          sharePointConnections.map(async (connection) => ({
            connectionId: connection.id,
            selections: await getIntegrationSelections(configurationSettings, connection.id),
          }))
        );

        if (isCancelled) {
          return;
        }

        const sharePointSources: IntegrationSource[] = selectionResponses.map(({ connectionId, selections }) => ({
          providerId: 'sharepoint',
          connectionIds: [connectionId],
          ...(selections.length > 0
            ? {
              resourceRefs: selections.map((selection) => ({
                nodeId: selection.source_node_id,
                nodeType: selection.node_type,
              })),
            }
            : {}),
        }));

        setSharePointSourcesCache(sharePointSources);
      } catch (error) {
        if (!isCancelled) {
          setSharePointSourcesCache([]);
          toast({
            variant: 'destructive',
            title: t('SOURCE_SELECTION_FAILED') || 'Source selection failed',
            description:
              error instanceof Error
                ? error.message
                : 'Failed to load selected SharePoint sources. Please try again.',
          });
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingSharePointSources(false);
        }
      }
    };

    void loadSharePointSources();

    return () => {
      isCancelled = true;
    };
  }, [configurationSettings, selectedSources, t, toast]);

  const resolveIntegrationSources = useCallback((): IntegrationSource[] => {
    const baseSources = buildBaseIntegrationSources();
    return [...baseSources, ...sharePointSourcesCache];
  }, [buildBaseIntegrationSources, sharePointSourcesCache]);

  const handleSubmit = async (e: React.FormEvent | KeyboardEvent) => {
    if (isSendBlocked) {
      return;
    }
    // const currentMessage =
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    const hasContent =
      message.trim().length > 0 || selectedSkills.length > 0 || files.length > 0;
    if (hasContent && !isLoading && onProcessing.length === 0) {
      // Dismiss mobile keyboard on Enter submit (button click already blurs via focus change).
      mentionsInputRef.current?.blur();

      handleTypingStop();
      const skillMentions = toMessageSkillMentions(selectedSkills);
      let cleanedMessage = message.replace(/^\s+|\s+$/g, '');
      const leadingWhitespaceLength = message.length - message.trimStart().length;
      cleanedMessage = cleanedMessage.replace(/\n/g, '  \n');

      const adjustedMentions =
        mentions.length > 0
          ? adjustMentionsForMarkdown(mentions, message, cleanedMessage, leadingWhitespaceLength)
          : undefined;


      if (isLoadingSharePointSources) {
        toast({
          variant: 'info',
          title: 'Loading SharePoint sources',
          description: 'Please wait a moment for the selected SharePoint sources to finish loading.',
        });
        return;
      }

      const sources = resolveIntegrationSources();

      // Clear immediately so the field resets while send/network work continues.
      setMessage('');
      setMessageMarkup('');
      setFiles([]);
      setReplyMessage(null);
      setMentions([]);
      setSelectedSkills([]);
      window.requestAnimationFrame(() => {
        adjustEditorHeight();
      });

      await onSendMessage(cleanedMessage, files, sources, adjustedMentions, selectedTools, skillMentions);

      if (isCreateImageActive) {
        onToggleTool?.(ButtonIds.CREATE_IMAGE);
      }
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // Check if plain text content exists in the clipboard (e.g. copied text, Excel cells, PPT text)
    const plainText = clipboardData.getData('text/plain') || clipboardData.getData('text');
    if (plainText && plainText.length > 0) {
      // Allow default text paste behavior and do NOT treat auto-generated clipboard images (from MS Office/HTML) as file uploads
      adjustEditorHeight();
      return;
    }

    const items = clipboardData.items;
    if (!items) return;

    const imageFiles: File[] = [];

    // Check for image items in clipboard when text content is absent (e.g. screenshots, copied images)
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the item is an image
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    // If we found image files, handle file upload
    if (imageFiles.length > 0) {
      e.preventDefault(); // Prevent default paste behavior only when actually uploading an image file

      // Check if the selected model allows image uploads (modelType === 1 doesn't support images)
      if (uploadModel?.modelType === 1) {
        toast({
          variant: 'destructive',
          title: t('IMAGE_NOT_SUPPORTED'),
          description: t('IMAGE_NOT_SUPPORTED_DESCRIPTION') || 'The selected model does not support image uploads. Please select a different model.',
        });
        return;
      }

      await onFilesSelected(imageFiles);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the container itself
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length === 0) return;

    if (!uploadModel) {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('NO_MODEL_SELECTED') || 'Please select a model first.',
      });
      return;
    }

    // Get supported extensions for the current model
    const supportedImageExtensions = modelSupportedExtensionsMap[uploadModel.modelType].image;
    const supportedDocumentExtensions = modelSupportedExtensionsMap[uploadModel.modelType].document;
    const allSupportedExtensions = [...supportedImageExtensions, ...supportedDocumentExtensions];

    // Separate valid files, images, unsupported files
    const validImageFiles: File[] = [];
    const validDocumentFiles: File[] = [];
    const unsupportedFiles: string[] = [];

    droppedFiles.forEach((file) => {
      const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (supportedImageExtensions.includes(extension)) {
        validImageFiles.push(file);
      } else if (supportedDocumentExtensions.includes(extension)) {
        validDocumentFiles.push(file);
      } else {
        unsupportedFiles.push(file.name);
      }
    });

    // Show error for unsupported files
    if (unsupportedFiles.length > 0) {
      toast({
        variant: 'destructive',
        title: t('UNSUPPORTED_FILE_TYPE'),
        description: (
          <div>
            <div>{t('UNSUPPORTED_FILES')}: {unsupportedFiles.join(', ')}.</div>
            <div className="mt-1">{t('SUPPORTED_EXTENSIONS')}: {allSupportedExtensions.join(', ')}</div>
          </div>
        ),
      });
    }

    // Check if model supports images
    if (validImageFiles.length > 0 && uploadModel.modelType === 1) {
      toast({
        variant: 'destructive',
        title: t('IMAGE_NOT_SUPPORTED'),
        description: t('IMAGE_NOT_SUPPORTED_DESCRIPTION') || 'The selected model does not support image uploads. Please select a different model.',
      });

      // Only process document files if any
      if (validDocumentFiles.length > 0) {
        await onFilesSelected(validDocumentFiles);
      }
      return;
    }

    // Process valid files
    const filesToProcess = uploadModel.modelType === 1
      ? validDocumentFiles
      : [...validImageFiles, ...validDocumentFiles];

    if (filesToProcess.length > 0) {
      await onFilesSelected(filesToProcess);
    }
  };



  const iconTypeButtonUi = (inputButton: ChatInputButton) => {
    const handleIconClick = () => {
      if (!inputButton.isSelected) {
        setFiles([]);
      }
      inputButton.onClick();
    };

    const button = (
      <button
        key={inputButton.id}
        aria-pressed="false"
        aria-label="Upload files and more"
        className={
          'flex h-8 min-w-8 items-center justify-center p-2 rounded-full border shadow-sm hover:bg-muted disabled:opacity-30 ' +
          (theme.theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200') +
          (inputButton.isDisabled ? 'cursor-not-allowed !opacity-30' : '') +
          (inputButton.isSelected ? 'border-primary !text-primary' : '')
        }
        onClick={handleIconClick}
        disabled={inputButton.isDisabled || onProcessing.length > 0}
      >
        {inputButton.icon}
      </button>
    );

    if (inputButton.id == ButtonIds.VOICE) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>
              <p className="text-xs text-gray-50">{t('COMING_SOON')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return button;
  };

  const buttonTypeButtonUi = (inputButton: ChatInputButton) => {
    const handleButtonClick = () => {
      if (!inputButton.isSelected) {
        setFiles([]);
      }
      inputButton.onClick();
    };

    const button = (
      <button
        key={inputButton.label}
        className={
          'flex h-8 min-w-8 items-center justify-center p-2 rounded-full border  shadow-sm hover:bg-muted disabled:opacity-30 ' +
          (theme.theme === 'dark' ? 'border-gray-500 opacity-70' : 'border-gray-200') +
          (inputButton.isDisabled ? '!cursor-not-allowed !opacity-30' : '') +
          (inputButton.isSelected ? 'bg-primary !border-primary !text-primary' : '')
        }
        data-testid="composer-button-search"
        aria-pressed="false"
        aria-label="Search"
        onClick={handleButtonClick}
        disabled={inputButton.isDisabled || onProcessing.length > 0}
      >
        {inputButton.icon}
        <span className="flex items-center justify-center">
          <div className="[display:var(--force-hide-label)] ps-1 pe-1 whitespace-nowrap text-sm">
            {inputButton.label}
          </div>
        </span>
      </button>
    );

    if (inputButton.id == ButtonIds.VOICE) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>
              <p className="text-xs text-gray-50">{t('COMING_SOON')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return button;
  };

  const undoSearchSelection = () => {
    const searchButton = inputButtons?.find((button) => button.id === ButtonIds.SEARCH);
    if (searchButton?.isSelected) {
      searchButton.onClick();
    }
  };

  const fileSizeExceedFilter = (files: File[]) => {
    return files.filter((file) => {
      const parts = file.name.split('.');
      const extension = parts[parts.length - 1];
      if (allSupportedImageExtensions.includes(`.${extension}`) && file.size > MAX_FILE_SIZE) {
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED') || 'Upload failed',
          description: `${t('MAX_SIZE_EXCEEDED_IMAGE') || 'File size exceeds 30MB limit'} (${file.name}).`,
        });
        return false;
      } else if (file.size > MAX_FILE_SIZE) {
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED') || 'Upload failed',
          description: `${t('MAX_SIZE_EXCEEDED_DESC') || 'File size exceeds 30MB limit'} (${file.name}).`,
        });
        return false;
      }
      return true;
    });
  };

  const onFilesSelected = async (selectedFiles: File[]) => {
    if (files?.length + selectedFiles?.length > 10) {
      toast({
        title: t('FILE_SELECTION_LIMIT_REACHED') || "File Selection Limit Reached",
        description: t('MAXIMUM_FILE_SELECTED_DESCRIPTION') || "You can only upload up to 10 files at once.",
        variant: "destructive",
      });
      return;
    }

    undoSearchSelection();
    selectedFiles = fileSizeExceedFilter(selectedFiles);
    if (selectedFiles.length === 0) {
      return;
    }
    const filesToProcess = selectedFiles.map((file) => {
      const itemId = uuidv4();
      const parts = file.name.split('.');
      const extension = parts[parts.length - 1];
      const attachment: Attachment = {
        fileId: itemId,
        fileName: file.name,
        extension: extension,
        sender: MessageRole.USER,
        size: file.size,
      };
      if (
        uploadModel &&
        modelSupportedExtensionsMap[uploadModel.modelType].image.includes(`.${extension}`)
      ) {
        setItemIdFilePathMap(itemId, URL.createObjectURL(file));
      }

      return { originalFile: file, attachmentData: attachment };
    });
    const newAttachments = filesToProcess.map((ftp) => ftp.attachmentData);
    setFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    setOnProcessing((prevFiles) => [...prevFiles, ...newAttachments]);

    for (const { originalFile, attachmentData } of filesToProcess) {
      setFileUploadingToastTitle(t('UPLOADING'));
      setFileUploadingToastDescription(` ${attachmentData.fileName} ${t('UPLOADING_FILE')}`);
      setFileUploadingToastOpen(true);

      try {
        const uploadResult = await uploadFileViaPresignedUrl(originalFile, attachmentData.fileId);

        setFileUploadingToastOpen(false);

        if (!uploadResult.isSuccess) {
          setFiles((prevFiles) =>
            prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
          );
          setOnProcessing((prevFiles) =>
            prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
          );
          console.error(uploadResult);
          toast({
            variant: 'destructive',
            title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
            description: `${attachmentData.fileName} ${t('NOT_UPLOADED_LOCAL_STORAGE')}.`,
          });
        } else {
          const isImageAttachment = Boolean(
            uploadModel &&
            modelSupportedExtensionsMap[uploadModel.modelType].image.includes(
              `.${attachmentData.extension}`
            )
          );
          const shouldVectorize =
            !isImageAttachment &&
            !VECTORIZE_EXCLUDE_EXTENSIONS.includes(attachmentData.extension) &&
            Boolean(
              uploadModel &&
              modelSupportedExtensionsMap[uploadModel.modelType].document.includes(
                `.${attachmentData.extension}`
              )
            );

          if (shouldVectorize && uploadModel) {
            setProcessingToastTitle(t('PROCESSING'));
            setProcessingToastDescription(
              `${attachmentData.fileName} ${t('PROCESSING_FILE_VECTOR')}`
            );
            setProcessingToastOpen(true);

            const vectorUploadItems: VectorUploadItem[] = [{ fileId: attachmentData.fileId }];
            try {
              await uploadFilesToVectorStorage(
                vectorUploadItems,
                uploadModel.id,
                configurationSettings
              );
              const timeoutId = setTimeout(() => {
                setProcessingToastOpen(false);

                setOnProcessing((prevFiles) =>
                  prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
                );

                toast({
                  variant: 'warning',
                  title: t('SUCCESS'),
                  description: `${attachmentData.fileName} ${t('UPLOADED_VECTOR_STORAGE_SUCCESS_TIMEOUT')}.`,
                });
              }, MAX_PROCESSING_TIME);
              addTimeout(attachmentData.fileId, timeoutId);
            } catch (err) {
              setProcessingToastOpen(false);

              setFiles((prevFiles) =>
                prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
              );
              setOnProcessing((prevFiles) =>
                prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
              );
              console.error(`vector upload failed with ${err}`);
              toast({
                variant: 'destructive',
                title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
                description: t('NOT_UPLOADED_VECTOR_STORAGE'),
              });
            }
          } else {
            setOnProcessing((prevFiles) =>
              prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
            );
          }
        }
      } catch (err: any) {
        setFileUploadingToastOpen(false);
        setFiles((prevFiles) => prevFiles.filter((file) => file.fileId !== attachmentData.fileId));
        setOnProcessing((prevFiles) =>
          prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
        );
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
          description:
            err?.status == 403
              ? t('YOU_DONT_HAVE_ENOUGH_PERMISSION_TO_UPLOAD_FILE')
              : `${attachmentData.fileName} ${t('NOT_UPLOADED_LOCAL_STORAGE')}.`,
        });
      }
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => {
      const nextFiles = prevFiles.filter((_, i) => i !== index);
      setIsSendDisabled(
        message.trim().length === 0 && selectedSkills.length === 0 && nextFiles.length === 0
      );
      return nextFiles;
    });
  };

  const mentionHighlightStyle = useMemo(
    () => ({
      color: 'hsl(var(--primary-500))',
      fontWeight: 400,
      display: 'inline',
      lineHeight: '160%',
      verticalAlign: 'baseline',
      padding: 0,
      margin: 0,
    }),
    [theme.theme]
  );

  const mentionsStyle = useMemo(() => {
    const isDark = theme.theme === 'dark';
    const placeholderColor = isDark ? 'hsl(var(--grey-600))' : '#6D6D6D';
    const inputTextColor = isDark ? '#FFFFFF' : '#000000';
    const inputFontFamily =
      'Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"';
    const inputTypography = {
      fontFamily: inputFontFamily,
      fontSize: '14px',
      fontStyle: 'normal' as const,
      fontWeight: 400,
      lineHeight: '160%',
      color: inputTextColor,
    };
    const { color: _inputTextColor, ...inputTypographyWithoutColor } = inputTypography;

    return {
      control: {
        minHeight: 48,
        maxHeight: 160,
        ...inputTypographyWithoutColor,
        color: inputTextColor,
        padding: 0,
        backgroundColor: 'transparent',
        overflow: 'hidden',
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
      },
      // Keep highlighter + textarea padding identical. Extra padding on one layer
      // only shifts wrap/caret so multi-line cursors drift by ~1ch.
      highlighter: {
        padding: '0px',
        paddingRight: '12px',
        margin: 0,
        border: 0,
        outline: 'none',
        ...inputTypographyWithoutColor,
        color: inputTextColor,
        maxHeight: 160,
        overflowX: 'hidden',
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      },
      input: {
        padding: '0px',
        paddingRight: '12px',
        margin: 0,
        border: 0,
        outline: 'none',
        caretColor: inputTextColor,
        ...inputTypographyWithoutColor,
        color: 'transparent',
        maxHeight: 160,
        overflowX: 'hidden',
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: isDark
          ? 'hsl(var(--muted-foreground) / 0.4) transparent'
          : 'hsl(var(--border) / 0.7) transparent',
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      },
      mention: {
        color: 'hsl(var(--primary-500))',
        fontWeight: 400,
        display: 'inline',
        lineHeight: '160%',
        verticalAlign: 'baseline',
        padding: 0,
        margin: 0,
      },
      // Default library styles set suggestions overlay background to white; set root
      // styles so rounded corners and theme background apply to the outer box (not just ul).
      suggestions: {
        zIndex: 50,
        pointerEvents: promptMention.active ? 'none' : 'auto',
        visibility: promptMention.active ? 'hidden' : 'visible',
        backgroundColor: 'hsl(var(--background))',
        border: '1px solid hsl(var(--border))',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow:
          '0 10px 15px -3px hsl(var(--foreground) / 0.08), 0 4px 6px -4px hsl(var(--foreground) / 0.08)',
        list: {
          margin: 0,
          padding: 0,
          listStyleType: 'none',
          backgroundColor: 'transparent',
          border: 'none',
          fontFamily:
            'Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '22px',
          maxHeight: 120,
          overflowY: 'auto',
          width: 320,
        },
        item: {
          padding: 0,
        },
        placeholder: {
          ...inputTypographyWithoutColor,
          color: placeholderColor,
        },
      },
    } as const;
  }, [theme.theme, promptMention.active]);

  const handleClearPromptFilter = () => {
    if (promptMention.startIndex < 0) return;
    const atEnd = promptMention.startIndex + 1;
    const before = message.slice(0, atEnd);
    const after = message.slice(promptMentionCursor);
    const nextMessage = before + after;
    setMessage(nextMessage);
    setMessageMarkup(nextMessage);
    setPromptMentionCursor(atEnd);
    setPromptMention({ active: true, query: '', startIndex: promptMention.startIndex });
    requestAnimationFrame(() => {
      mentionsInputRef.current?.setSelectionRange(atEnd, atEnd);
    });
  };

  const insertPromptIntoMessage = (insertContext: PromptInsertContext, promptBody: string) => {
    const nextMessage = buildMessageWithPrompt(insertContext, promptBody);
    const nextCursor = getCursorAfterPromptInsert(insertContext, promptBody);
    setMessage(nextMessage);
    setMessageMarkup(nextMessage);
    setTimeout(() => {
      const textarea = mentionsInputRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
      syncEditorHeight();
    }, 0);
  };

  const toPromptItem = (item: MentionPickerItem): PromptItem => ({
    id: item.id,
    folderId: '',
    title: item.title,
    body: item.body,
    createdAt: '',
    updatedAt: '',
  });

  const applyClearedMentionMessage = (insertContext: PromptInsertContext) => {
    const nextMessage = clearMentionTrigger(insertContext);
    const nextCursor = insertContext.before.length;
    setMessage(nextMessage);
    setMessageMarkup(nextMessage);
    setPromptMentionCursor(nextCursor);
    setTimeout(() => {
      const textarea = mentionsInputRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
      syncEditorHeight();
    }, 0);
  };

  const requestAddSkillMention = () => {
    const needsSpace = message.length > 0 && !message.endsWith(' ');
    const nextMessage = `${message}${needsSpace ? ' ' : ''}${CONTEXT_MENTION_TRIGGER}`;
    const nextCursor = nextMessage.length;
    setMessage(nextMessage);
    setMessageMarkup(nextMessage);
    setPromptMentionCursor(nextCursor);
    setPromptMention(getPromptMentionState(nextMessage, nextCursor));
    setMentionPickerView('categories');
    setMentionCategory(null);
    requestAnimationFrame(() => {
      mentionsInputRef.current?.focus();
      mentionsInputRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handlePlusMenuSkillSelect = (skill: MentionPickerItem) => {
    setSelectedSkills((current) => appendSkillToSelection(current, skill));
    setIsSendDisabled(false);
  };

  const handleOpenNewProjectModal = () => {
    globalThis.setTimeout(() => setIsNewProjectModalOpen(true), 0);
  };

  const handleToggleConnectorsTool = () => {
    if (externalIsSourcesToolActive !== undefined) {
      externalOnToggleSourcesTool?.();
    } else {
      setInternalIsSourcesToolActive(!internalIsSourcesToolActive);
      externalOnToggleSourcesTool?.();
    }
  };

  const handlePromptMentionSelect = (item: MentionPickerItem) => {
    const insertContext =
      mentionInsertRef.current ??
      buildPromptInsertContext(message, promptMention, promptMentionCursor);
    if (!insertContext) return;

    setPromptMention({ active: false, query: '', startIndex: -1 });
    setMentionPickerView('categories');
    setMentionCategory(null);
    mentionInsertRef.current = null;

    if (item.type === 'skill') {
      applyClearedMentionMessage(insertContext);
      setSelectedSkills((current) => appendSkillToSelection(current, item));
      setIsSendDisabled(false);
      return;
    }

    if (extractVariables(item.body).length > 0) {
      setApplyPromptState({ prompt: toPromptItem(item), insertContext });
      return;
    }

    insertPromptIntoMessage(insertContext, item.body);
  };

  const handleApplyPromptFromModal = (resolvedBody: string) => {
    if (!applyPromptState) return;
    insertPromptIntoMessage(applyPromptState.insertContext, resolvedBody);
    setApplyPromptState(null);
  };

  const closePromptMentionPicker = () => {
    setPromptMention({ active: false, query: '', startIndex: -1 });
    setMentionPickerView('categories');
    setMentionCategory(null);
  };

  const handlePromptMentionEscape = () => {
    if (promptMention.query.trim()) {
      handleClearPromptFilter();
      setMentionPickerView('categories');
      setMentionCategory(null);
      return;
    }
    if (mentionPickerView === 'items') {
      setMentionPickerView('categories');
      setMentionCategory(null);
      return;
    }
    closePromptMentionPicker();
  };

  const handleMentionsChange: OnChangeHandlerFunc = (_e, newValue, newPlainTextValue, reactMentions) => {
    const plainText = isGroupChat ? collapseDuplicateAtSigns(newPlainTextValue) : newPlainTextValue;
    const plainChanged = plainText !== newPlainTextValue;

    setMessage(plainText);

    const cursor =
      mentionsInputRef.current?.selectionStart ?? plainText.length;
    setPromptMentionCursor(cursor);
    if (!isGroupChat) {
      setMessageMarkup(newValue);
      setPromptMention(getPromptMentionState(plainText, cursor));
      updateAgentMention(plainText, cursor);
    } else {
      setPromptMention({ active: false, query: '', startIndex: -1 });
    }

    // Parse mentions detected by react-mentions
    const parsedMentions = isGroupChat
      ? mapReactMentionsToMentions(reactMentions as Array<{ id: string; display: string; plainTextIndex: number }>)
      : [];

    // Also scan for @mentions in plain text that weren't detected by react-mentions
    // (e.g., from pasted text that wasn't selected via the dropdown)
    if (isGroupChat && plainText) {
      // Sort users by name length (descending) to match longer names first
      const sortedUsers = [...mentionUsers].sort((a, b) => {
        const nameA = `${a.firstName ?? ''}${a.lastName ? ` ${a.lastName}` : ''}`.trim();
        const nameB = `${b.firstName ?? ''}${b.lastName ? ` ${b.lastName}` : ''}`.trim();
        return nameB.length - nameA.length;
      });

      // Track positions already covered by existing mentions
      const coveredRanges = parsedMentions.map(m => ({ start: m.start, end: m.end }));

      for (const user of sortedUsers) {
        const fullName = `${user.firstName ?? ''}${user.lastName ? ` ${user.lastName}` : ''}`.trim();
        if (!fullName) continue;

        // Escape special regex characters in name
        const escapedName = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Match @Name (case-insensitive) that's not followed by alphanumeric or ]
        const pattern = new RegExp(`@${escapedName}(?![\\]\\w])`, 'gi');

        let match;
        while ((match = pattern.exec(plainText)) !== null) {
          const start = match.index;
          const end = start + match[0].length;

          // Check if this position is already covered by an existing mention
          const isAlreadyCovered = coveredRanges.some(
            range => (start >= range.start && start < range.end) || (end > range.start && end <= range.end)
          );

          if (!isAlreadyCovered) {
            // Add this as an additional mention
            parsedMentions.push({
              userId: user.itemId,
              trigger: `@${fullName}`,
              start,
              end,
              user: mentionUsersMap.get(user.itemId),
            });

            // Mark this range as covered
            coveredRanges.push({ start, end });
          }
        }
      }

      // Sort mentions by start position
      parsedMentions.sort((a, b) => a.start - b.start);
    }

    if (isGroupChat) {
      if (hasGroupChatMentionMarkup(newValue, plainText) && !plainChanged) {
        setMessageMarkup(newValue);
      } else if (parsedMentions.length > 0) {
        setMessageMarkup(buildGroupChatMentionMarkup(plainText, parsedMentions));
      } else {
        setMessageMarkup(plainText);
      }
    }

    setMentions(parsedMentions);
    setIsSendDisabled(
      plainText.trim().length === 0 && selectedSkills.length === 0 && files.length === 0
    );

    if (plainText.length > 0 && activeConversationId && isInputFocusedRef.current) {
      if (!isTyping) {
        sendTypingStatus(activeConversationId, true);
        setIsTyping(true);
      }
    }

    // Height adjust
    adjustEditorHeight();
  };

  const isSelectDocumentChipShow = selectedSources?.find(source => source === 'document-manager') ? true : false;

  const quickActionButtons = useMemo(
    () => inputButtons?.filter((button) => button.alignment === Alignment.LEFT) ?? [],
    [inputButtons]
  );

  const handleQuickActionClick = (inputButton: ChatInputButton) => {
    if (!inputButton.isSelected) {
      setFiles([]);
    }
    inputButton.onClick();
  };

  const quickActions = quickActionButtons.map((button) => ({
    ...button,
    onClick: () => handleQuickActionClick(button),
  }));

  const handleSelectImageIdea = (idea: ImageIdea) => {
    setMessage(idea.prompt);
    setMessageMarkup(idea.prompt);
    setIsSendDisabled(false);
    setTimeout(() => {
      mentionsInputRef.current?.focus();
      adjustEditorHeight();
    }, 0);
  };

  const handleSelectWritePrompt = (item: WriteOrEditPrompt) => {
    setMessage(item.prompt);
    setMessageMarkup(item.prompt);
    setIsSendDisabled(false);

    if (isWriteOrEditActive) {
      onToggleTool?.(ButtonIds.WRITE_OR_EDIT);
    }

    setTimeout(() => {
      mentionsInputRef.current?.focus();
      adjustEditorHeight();
    }, 0);
  };

  const handleSelectDocument = (folderIds: string[]) => {
    setSelectedFolderIdsBySource({
      ...selectedFolderIdsBySource,
      'document-manager': folderIds,
    });
  };

  const dismissWriteOrEditPanel = useCallback(() => {
    if (isWriteOrEditActive) {
      onToggleTool?.(ButtonIds.WRITE_OR_EDIT);
    }
  }, [isWriteOrEditActive, onToggleTool]);

  const dismissAgentsPanel = useCallback(() => {
    if (isAgentsActive) {
      onToggleTool?.(ButtonIds.AGENTS);
    }
  }, [isAgentsActive, onToggleTool]);

  useEffect(() => {
    if (!showWriteOrEditPrompts) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (expandedQuickPanelRef.current?.contains(target)) {
        return;
      }

      dismissWriteOrEditPanel();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showWriteOrEditPrompts, dismissWriteOrEditPanel]);

  useEffect(() => {
    if (!showAgents) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (expandedQuickPanelRef.current?.contains(target)) {
        return;
      }

      dismissAgentsPanel();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showAgents, dismissAgentsPanel]);

  const { projectId } = useParams();
  const isAgentProjectTabVisible = showAgentSelector && showAgentProjectTab;
  const renderAgentSelector = (variant?: 'tab') => (
    <AgentSelector
      selectedAgentId={selectedAgentId ?? null}
      onSelect={(agentId) => onAgentChange?.(agentId, message)}
      isDisabled={isLoading || isCurrentResponseGenerating}
      variant={variant}
    />
  );

  return (
    <>
      <CustomToast
        open={processingToastOpen}
        onOpenChange={setProcessingToastOpen}
        title={processingToastTitle}
        description={processingToastDescription}
        variant="warning"
        preventAutoClose={true}
      />

      <CustomToast
        open={fileUploadingToastOpen}
        onOpenChange={setFileUploadingToastOpen}
        title={fileUploadingToastTitle}
        description={fileUploadingToastDescription}
        variant="warning"
        preventAutoClose={true}
      />
      <div ref={outerContainerRef} data-buddy-tour-target="composer" className="relative flex w-full flex-col gap-4">
        <UsageLimitBanner />
        <div className="flex flex-col">
        {isAgentProjectTabVisible && (
          <ChatInputStepHeader>
            <div className="flex min-w-0 items-center gap-4">
              {!projectId && (
                <>
                  {renderAgentSelector('tab')}
                  <span
                    aria-hidden
                    className={`h-4 w-px shrink-0 ${theme.theme === 'dark' ? 'bg-[#4A4A4A]' : 'bg-grey-200'}`}
                  />
                </>
              )}
              <ProjectSelector
                disabled={isLoading || isCurrentResponseGenerating || agentUnavailable}
              />
            </div>
          </ChatInputStepHeader>
        )}
        <div
          style={{
            boxShadow:
              theme.theme === 'dark'
                ? '0 10px 20px 0 rgba(0, 0, 0, 0.35)'
                : '0 10px 20px 0 rgba(173, 174, 163, 0.2)',
          }}
          ref={containerRef}
          className={`relative w-full min-w-0 max-w-full min-h-0 sm:min-h-[122px] rounded-[16px] sm:rounded-[24px] border-solid px-3 pt-2 pb-1.5 sm:p-5 ${theme.theme === 'dark'
            ? 'bg-black-100'
            : 'border border-[#E7E7E7] bg-[#FDFDFD]'
            } ${isDragging ? 'border-2 border-primary bg-primary/5' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        // onClick={connect}
        >
          {/* Drag and Drop Overlay */}
          {isDragging && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/10 backdrop-blur-sm rounded-2xl border-2 border-dashed border-primary">
              <div className="text-center">
                <div className="text-primary font-semibold text-lg mb-2">
                  {t('DROP_FILES_HERE') || 'Drop files here'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {uploadModel?.modelType === 1
                    ? t('DOCUMENTS_ONLY') || 'Documents only'
                    : t('IMAGES_AND_DOCUMENTS') || 'Images and documents supported'}
                </div>
              </div>
            </div>
          )}
          {replyMessage && (
            <div className={`mb-3 pb-3 border-b ${theme.theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${theme.theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                    {replyMessage.repliedUser
                      ? `${replyMessage.repliedUser.firstName}${replyMessage.repliedUser.lastName ? ` ${replyMessage.repliedUser.lastName}` : ''}`
                      : 'User'}
                  </span>
                  {(() => {
                    const d = parseMessageTimeForDisplay(
                      replyMessage.generation.showMessageTime,
                      replyMessage.generation.createdDate
                    );
                    if (!d) return null;
                    return (
                      <span className={`text-xs ${theme.theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatDate(d)}
                      </span>
                    );
                  })()}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setReplyMessage(null);
                  }}
                  className="flex-shrink-0 p-1 rounded-full hover:bg-muted transition-colors"
                  aria-label="Cancel reply"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
              <div className={`text-sm ${theme.theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} truncate`}>
                {replyMessage.generation.content || 'Message with attachments'}
              </div>
            </div>
          )}

          {/* Selected Sources Display - Now handled by ToolsSelector */}

          {files.length > 0 && (
            <div className="mb-3">
              <AttachmentFileCardList
                files={files}
                isDark={theme.theme === 'dark'}
                previewUrlById={itemIdFilePathMap}
                onRemove={removeFile}
                isProcessingFile={(file) =>
                  onProcessing.some((pFile) => pFile.fileId === file.fileId)
                }
              />
            </div>
          )}

          {!isDictationOnGoing && (
            <form className="mx-auto flex items-center gap-4" onSubmit={handleSubmit}>
              <div className="relative w-full min-w-0">
                {!isGroupChat && (
                  <SelectedSkillChips
                    skills={selectedSkills}
                    onRemove={(skillId) => {
                      setSelectedSkills((current) => {
                        const next = current.filter((skill) => skill.id !== skillId);
                        setIsSendDisabled(
                          message.trim().length === 0 && next.length === 0 && files.length === 0
                        );
                        return next;
                      });
                    }}
                    onAddClick={requestAddSkillMention}
                  />
                )}
                {!isGroupChat && (
                  <ContextMentionPicker
                    open={promptMention.active}
                    view={mentionPickerView}
                    onViewChange={setMentionPickerView}
                    category={mentionCategory}
                    onCategoryChange={setMentionCategory}
                    categories={promptMentionCategories}
                    items={promptMentionResults}
                    isLoading={isPromptMentionLoading}
                    isCategoriesLoading={isPromptMentionCategoriesLoading}
                    query={promptMention.query}
                    onSelect={handlePromptMentionSelect}
                    onClearFilter={handleClearPromptFilter}
                    onClose={closePromptMentionPicker}
                    inputRef={mentionsInputRef}
                  />
                )}
                <AgentMentionPicker
                  open={agentMention.active}
                  query={agentMention.query}
                  onSelect={(agentId) => selectAgentMention(message, agentId)}
                  onClose={closeAgentMention}
                  inputRef={mentionsInputRef}
                />
                <style>
                  {`
                  .chat-mentions-input__highlighter__placeholder {
                    color: ${theme.theme === 'dark' ? 'hsl(var(--grey-600))' : '#6D6D6D'} !important;
                    font-family: Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji" !important;
                    font-size: 14px !important;
                    font-weight: 400 !important;
                    line-height: 160% !important;
                    opacity: ${showRotatingPlaceholder ? placeholderOpacity : 0};
                    transition: opacity 0.3s ease-in-out;
                  }
                  .chat-mentions-input__input::placeholder {
                    color: ${theme.theme === 'dark' ? 'hsl(var(--grey-600))' : '#6D6D6D'} !important;
                    -webkit-text-fill-color: ${theme.theme === 'dark' ? 'hsl(var(--grey-600))' : '#6D6D6D'};
                    font-family: Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji" !important;
                    font-size: 14px !important;
                    font-weight: 400 !important;
                    line-height: 160% !important;
                    opacity: ${showRotatingPlaceholder ? placeholderOpacity : 0};
                    transition: opacity 0.3s ease-in-out;
                  }
                  @media (max-width: 639px) {
                    .chat-mentions-input__highlighter__placeholder,
                    .chat-mentions-input__input::placeholder {
                      font-size: 13px !important;
                    }
                    .chat-mentions-input__highlighter,
                    .chat-mentions-input__input {
                      font-size: 14px !important;
                    }
                  }
                  @media (min-width: 1536px) {
                    .chat-mentions-input__highlighter__placeholder,
                    .chat-mentions-input__input::placeholder,
                    .chat-mentions-input__highlighter,
                    .chat-mentions-input__input {
                      font-size: 14px !important;
                    }
                  }
                  .chat-mentions-input__highlighter__substring {
                    visibility: visible !important;
                  }
                  .chat-mentions-input__input:not(:placeholder-shown) {
                    color: transparent;
                    -webkit-text-fill-color: transparent;
                  }
                  .chat-mentions-input__highlighter,
                  .chat-mentions-input__input {
                    box-sizing: border-box;
                    font-family: Poppins, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 160%;
                    padding-top: 0 !important;
                    padding-bottom: 0 !important;
                    padding-right: 12px !important;
                    overflow-wrap: anywhere;
                    white-space: pre-wrap;
                    word-break: break-word;
                  }

                  .chat-mentions-input__highlighter {
                    color: ${theme.theme === 'dark' ? '#FFFFFF' : '#000000'};
                    scrollbar-width: thin;
                    scrollbar-color: transparent transparent;
                  }
                  .chat-mentions-input__highlighter::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                  }
                  .chat-mentions-input__highlighter::-webkit-scrollbar-thumb {
                    background: transparent;
                  }
                  .chat-mentions-input__input {
                    scrollbar-width: thin !important;
                    scrollbar-color: ${theme.theme === 'dark'
                      ? 'hsl(var(--muted-foreground) / 0.4) transparent'
                      : 'hsl(var(--border) / 0.7) transparent'};
                  }
                  .chat-mentions-input__input::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                  }
                  .chat-mentions-input__input::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  .chat-mentions-input__input::-webkit-scrollbar-thumb {
                    background: ${theme.theme === 'dark'
                      ? 'hsl(var(--muted-foreground) / 0.4)'
                      : 'hsl(var(--border) / 0.7)'};
                    border-radius: 9999px;
                  }
                  .chat-mentions-input__highlighter strong {
                    display: inline;
                    line-height: 160%;
                    vertical-align: baseline;
                    font-weight: 400;
                    padding: 0;
                    margin: 0;
                    background: none;
                    border-radius: 0;
                  }
                `}
                </style>
                <MentionsInput
                  value={messageMarkup}
                  onPaste={handlePaste}
                  onChange={handleMentionsChange}
                  inputRef={(el: HTMLTextAreaElement | null) => {
                    mentionsInputRef.current = el;
                  }}
                  placeholder={activePlaceholder}
                  disabled={agentUnavailable || (!hasParticipants && isLoading && !isCurrentResponseGenerating)}
                  forceSuggestionsAboveCursor={true}
                  style={mentionsStyle as any}
                  className="chat-mentions-input"
                  onKeyDown={(e) => {
                    if (!isGroupChat && promptMention.active && e.key === 'Escape') {
                      e.preventDefault();
                      handlePromptMentionEscape();
                      return;
                    }
                    if (
                      e.key === 'Enter' &&
                      !e.shiftKey &&
                      !isSendBlocked &&
                      !agentMention.active &&
                      (!promptMention.active ||
                        isGroupChat ||
                        (mentionPickerView === 'categories' && !promptMention.query.trim()))
                    ) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  onFocus={() => {
                    isInputFocusedRef.current = true;
                  }}
                  onBlur={() => {
                    isInputFocusedRef.current = false;
                    if (isTyping && activeConversationId) {
                      handleTypingStop();
                    }
                  }}
                >
                  {isGroupChat ? (
                    <ReactMention
                      trigger="@"
                      data={mentionDataProvider}
                      appendSpaceOnAdd={true}
                      style={mentionHighlightStyle}
                      markup="@__[__display__](__id__)"
                      // Ensure the rendered text includes the '@' prefix (single @)
                      displayTransform={(_id: string, display: string) => `@${display}`}
                      renderSuggestion={(
                        suggestion: SuggestionDataItem,
                        _search: string,
                        highlightedDisplay: React.ReactNode,
                        _index: number,
                        focused: boolean
                      ) => {
                        const user = mentionUsersMap.get(String(suggestion.id));
                        const displayName =
                          user
                            ? `${user.firstName ?? ''}${user.lastName ? ` ${user.lastName}` : ''}`.trim()
                            : String(suggestion.display ?? suggestion.id);
                        const email = user?.email ?? '';
                        const avatarSrc = user?.profileImageUrl || seliseAILogo;

                        return (
                          <div
                            className={[
                              'flex items-center gap-2 p-2 rounded-lg cursor-pointer w-full text-left transition-all border border-transparent',
                              focused ? 'bg-muted border-border' : 'hover:bg-muted hover:border-border',
                            ].join(' ')}
                          >
                            <div className="flex-shrink-0">
                              <img
                                src={avatarSrc}
                                alt={displayName || 'user'}
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">
                                {highlightedDisplay ? (
                                  <>
                                    @
                                    {highlightedDisplay}
                                  </>
                                ) : (
                                  `@${displayName}`
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {email}
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  ) : (
                    <ReactMention trigger="$" markup="$[__display__](__id__)" data={[]} />
                  )}
                </MentionsInput>
              </div>
            </form>
          )}
          {inputButtons && inputButtons.length > 0 && (
            <div
              className={`flex flex-row rounded-b-[16px] sm:rounded-none flex-wrap justify-between gap-2 -mx-3 sm:-mx-5 px-3 sm:px-5 pt-1 mt-0 sm:pt-2 sm:mt-1 border-t backdrop-blur-[12px] ${theme.theme === 'dark' ? 'border-black-500' : 'border-[#F6F6F6]'}`}
            >
              {!isDictationOnGoing && showMoreOptions && <div className="flex flex-wrap justify-start gap-2 xl:gap-4 items-center">
                <Popover open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} modal={false}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`-ml-2 h-7 w-7 hover:rounded-full hover:bg-muted xl:hidden flex items-center justify-center 2xl:h-8 2xl:w-8 [&_svg]:!size-6 2xl:[&_svg]:!size-7`}
                    >
                      <Plus
                        width={32}
                        height={32}
                        strokeWidth={1}
                        className={`!size-6 shrink-0 2xl:!size-7 ${theme.theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`}
                      />
                      <span className="sr-only">{t('MORE_OPTIONS') || 'More options'}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="pointer-events-auto z-[140] flex w-52 max-h-[min(70vh,28rem,var(--radix-popover-content-available-height))] flex-col overflow-visible p-0"
                    align="start"
                    side="top"
                    sideOffset={10}
                    collisionPadding={16}
                    onInteractOutside={(e) => {
                      const target = e.target as HTMLElement;
                      if (
                        target.closest('[data-radix-popper-content-wrapper]') ||
                        target.closest('[data-sources-selector]') ||
                        target.closest('[data-plus-menu-flyout]') ||
                        target.closest('[data-tools-selector-flyout]') ||
                        target.closest('[data-tools-selector-dialog]')
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onPointerDownOutside={(event) => {
                      const target = event.target as HTMLElement | null;
                      if (
                        target?.closest('[data-plus-menu-flyout]') ||
                        target?.closest('[data-tools-selector-flyout]') ||
                        target?.closest('[data-tools-selector-dialog]') ||
                        target?.closest('[data-radix-dialog-overlay]') ||
                        target?.closest('[data-radix-dialog-content]')
                      ) {
                        event.preventDefault();
                      }
                    }}
                  >
                    <div className="sticky top-0 z-10 flex shrink-0 items-center justify-end border-b border-border bg-popover px-2 py-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMobileMenuOpen(false);
                        }}
                        className="flex-shrink-0 p-1 rounded-full hover:bg-muted transition-colors"
                        aria-label={t('CLOSE') || 'Close'}
                      >
                        <X className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>

                    {/* One scroll area so the menu fits short phone screens instead of overflowing the viewport. */}
                    <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="px-3 pt-3">
                      {/* File Upload */}
                      <div
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className=""
                      >
                        <div onClick={(e) => e.stopPropagation()}>
                          <FileUploadPopover
                            modelType={uploadModel?.modelType}
                            onFilesSelected={(files) => {
                              onFilesSelected?.(files);
                              setMobileMenuOpen(false);
                            }}
                            isFeatureSelected={isFeatureSelected}
                            disabled={selectedSources.length > 0}
                            showExtrasOnMobile={false}
                            onSelectSkill={(skill) => {
                              handlePlusMenuSkillSelect(skill);
                              setMobileMenuOpen(false);
                            }}
                            onOpenNewProject={() => {
                              handleOpenNewProjectModal();
                              setMobileMenuOpen(false);
                            }}
                            onToggleConnectors={() => {
                              handleToggleConnectorsTool();
                              setMobileMenuOpen(false);
                            }}
                            isConnectorsActive={isSourcesToolActive}
                            onMenuClose={() => setMobileMenuOpen(false)}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="relative px-3 py-1">
                      <div className="h-px bg-border -mx-1 my-2" />

                      <ChatPlusMenuExtras
                        section="main"
                        useTapSubmenus
                        onClose={() => setMobileMenuOpen(false)}
                        onSelectSkill={(skill) => {
                          handlePlusMenuSkillSelect(skill);
                          setMobileMenuOpen(false);
                        }}
                        onOpenNewProject={() => {
                          handleOpenNewProjectModal();
                          setMobileMenuOpen(false);
                        }}
                        onToggleConnectors={() => {
                          handleToggleConnectorsTool();
                          setMobileMenuOpen(false);
                        }}
                        isConnectorsActive={isSourcesToolActive}
                      />
                    </div>

                    <div className="px-3 pb-1">
                      <div className="h-px bg-border -mx-1 my-2" />

                      {/* Tools Selector */}
                      <div
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className=""
                      >
                        <div
                          onPointerDown={(e) => {
                            // Allow sources button clicks to work
                            const target = e.target as HTMLElement;
                            if (target.closest('button[aria-label="Select sources"]') ||
                              target.closest('[data-sources-button]') ||
                              target.closest('[data-radix-popper-content-wrapper]')) {
                              return; // Don't stop propagation for sources button
                            }
                            e.stopPropagation();
                          }}
                          onMouseDown={(e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest('button[aria-label="Select sources"]') ||
                              target.closest('[data-sources-button]') ||
                              target.closest('[data-radix-popper-content-wrapper]')) {
                              return;
                            }
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest('button[aria-label="Select sources"]') ||
                              target.closest('[data-sources-button]') ||
                              target.closest('[data-radix-popper-content-wrapper]')) {
                              return;
                            }
                            e.stopPropagation();
                          }}
                        >
                          <ToolsSelector
                            isFeatureSelected={isFeatureSelected}
                            onToggleTool={(toolId: ButtonIds) => {
                              onToggleTool?.(toolId);
                              setMobileMenuOpen(false);
                            }}
                            disabled={false}
                            onOpenSourcesSelector={() => {
                              setSourcesPopoverOpen(true);
                              // Don't close mobile menu - let SourcesSelector open and handle closing
                            }}
                            selectedSources={selectedSources}
                            isSourcesToolActive={isSourcesToolActive}
                            onToggleSourcesTool={() => {
                              if (externalIsSourcesToolActive !== undefined) {
                                externalOnToggleSourcesTool?.();
                              } else {
                                setInternalIsSourcesToolActive(!internalIsSourcesToolActive);
                                externalOnToggleSourcesTool?.();
                              }
                              setMobileMenuOpen(false);
                            }}
                            onSourcesChange={setSelectedSources}
                            sourcesPopoverOpen={sourcesPopoverOpen}
                            onSourcesPopoverOpenChange={(open) => {
                              setSourcesPopoverOpen(open);
                              if (open) {
                                // Close mobile menu when sources popover opens, but with delay
                                setTimeout(() => setMobileMenuOpen(false), 150);
                              }
                            }}
                            selectedTools={selectedTools}
                            onSelectedToolsChange={onSelectedToolsChange}
                            onMenuClose={() => setMobileMenuOpen(false)}
                          />
                        </div>
                      </div>
                    </div>
                    </div>

                    <div className="shrink-0 border-t border-border bg-popover px-3 pb-3 pt-1">
                      <ChatPlusMenuExtras section="footer" showLeadingSeparator={false} />
                    </div>

                    {/* Source Document Selector */}
                    {/* {isSelectDocumentChipShow && (
                      <>
                        <div className="h-px bg-border -mx-1 " />
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center justify-start"
                        >
                          <SourceDocumentSelector onSelect={handleSelectDocument} />
                        </div>
                      </>
                    )} */}
                  </PopoverContent>
                </Popover>

                <div className="xl:hidden flex items-center gap-2 ml-0 xl:ml-2">
                  <ActiveToolsDisplay
                    className="flex items-center gap-2"
                    isFeatureSelected={isFeatureSelected}
                    onToggleTool={(toolId: ButtonIds) => onToggleTool?.(toolId)}
                    onOpenSourcesSelector={() => setSourcesPopoverOpen(true)}
                    selectedSources={selectedSources}
                    isSourcesToolActive={isSourcesToolActive}
                    onToggleSourcesTool={() => {
                      if (externalIsSourcesToolActive !== undefined) {
                        externalOnToggleSourcesTool?.();
                      } else {
                        setInternalIsSourcesToolActive(!internalIsSourcesToolActive);
                        externalOnToggleSourcesTool?.();
                      }
                    }}
                    onSourcesChange={setSelectedSources}
                    sourcesPopoverOpen={sourcesPopoverOpen}
                    onSourcesPopoverOpenChange={setSourcesPopoverOpen}
                    selectedTools={selectedTools}
                    onSelectedToolsChange={onSelectedToolsChange}
                  />
                  {isSelectDocumentChipShow && (
                    <SourceDocumentSelector
                      selectedFolderIds={selectedFolderIdsBySource['document-manager'] ?? []}
                      onSelect={handleSelectDocument}
                    />
                  )}
                </div>
                {/* <div className={`w-[0.5px] h-6 ${theme.theme === 'dark' ? 'bg-primary-200' : 'bg-primary'} xl:block hidden`} /> */}
                <div className="xl:flex hidden items-center">
                  <FileUploadPopover
                    modelType={uploadModel?.modelType}
                    onFilesSelected={onFilesSelected}
                    isFeatureSelected={isFeatureSelected}
                    disabled={selectedSources.length > 0}
                    onSelectSkill={handlePlusMenuSkillSelect}
                    onOpenNewProject={handleOpenNewProjectModal}
                    onToggleConnectors={handleToggleConnectorsTool}
                    isConnectorsActive={isSourcesToolActive}
                  />
                  <ToolsSelector
                    isFeatureSelected={isFeatureSelected}
                    onToggleTool={(toolId: ButtonIds) => onToggleTool?.(toolId)}
                    disabled={false}
                    onOpenSourcesSelector={() => setSourcesPopoverOpen(true)}
                    selectedSources={selectedSources}
                    isSourcesToolActive={isSourcesToolActive}
                    onToggleSourcesTool={() => {
                      if (externalIsSourcesToolActive !== undefined) {
                        externalOnToggleSourcesTool?.();
                      } else {
                        setInternalIsSourcesToolActive(!internalIsSourcesToolActive);
                        externalOnToggleSourcesTool?.();
                      }
                    }}
                    onSourcesChange={setSelectedSources}
                    sourcesPopoverOpen={sourcesPopoverOpen}
                    onSourcesPopoverOpenChange={setSourcesPopoverOpen}
                    selectedTools={selectedTools}
                    onSelectedToolsChange={onSelectedToolsChange}
                  />
                  {isSelectDocumentChipShow && (
                    <SourceDocumentSelector
                      selectedFolderIds={selectedFolderIdsBySource['document-manager'] ?? []}
                      onSelect={handleSelectDocument}
                    />
                  )}
                </div>

              </div>}
              {/* On mobile, mic + send stay on the + row (top-right); agent/model wrap below. */}
              <div
                className={
                  (isDictationOnGoing
                    ? 'flex w-full min-w-0 '
                    : 'ml-auto flex max-sm:contents ') +
                  'flex-wrap items-center justify-end gap-2'
                }
              >
                <div className="flex min-w-0 flex-nowrap items-center gap-2 max-sm:order-last max-sm:w-full">
                  {inputButtons
                    .filter((button) => button.alignment === Alignment.RIGHT)
                    .map((inputButton) =>
                      inputButton.type === ButtonTypes.BUTTON
                        ? buttonTypeButtonUi(inputButton)
                        : iconTypeButtonUi(inputButton)
                    )}
                  {showAgentSelector && !showAgentProjectTab && renderAgentSelector()}
                  <ModelSelector
                    isDisabled={
                      !!isModelSelectionDisabled ||
                      isLoading ||
                      isCurrentResponseGenerating ||
                      agentUnavailable
                    }
                  />
                  <UsageLimitCircle isBusy={isLoading || isCurrentResponseGenerating} />
                </div>
                <div
                  className={
                    isDictationOnGoing
                      ? 'flex min-w-0 w-full items-center gap-2'
                      : 'flex shrink-0 items-center gap-2 max-sm:ml-auto'
                  }
                >
                  {onStartVoiceCall && !isDictationOnGoing && (
                    <VoiceCallButton
                      onClick={onStartVoiceCall}
                      isActive={isVoiceCallActive}
                      disabled={
                        isLoading || isCurrentResponseGenerating || agentUnavailable || isVoiceCallActive
                      }
                    />
                  )}
                  <DictateButton
                    resetKey={chatTargetKey}
                    isDictationOnGoing={isDictationOnGoing}
                    setIsDictationOnGoing={setIsDictationOnGoing}
                    disabled={
                      isLoading || isCurrentResponseGenerating || agentUnavailable
                    }
                    onTranscribe={(newMessage) => {
                      if (!newMessage) return;
                      const appendText = (prev: string, addition: string) => {
                        if (!prev) return addition;
                        const separator = prev.endsWith(' ') || prev.endsWith('\n') ? '' : ' ';
                        return `${prev}${separator}${addition}`;
                      };

                      // Append dictation text into MentionsInput (preserves existing mention markup)
                      setMessageMarkup((prev) => appendText(prev, newMessage));
                      setMessage((prev) => appendText(prev, newMessage));
                      setIsSendDisabled(false);
                      setTimeout(() => {
                        const textarea = mentionsInputRef.current;
                        if (textarea) {
                          textarea.focus();
                          const len = textarea.value.length;
                          textarea.setSelectionRange(len, len);
                          textarea.scrollTop = textarea.scrollHeight;
                        }
                        adjustEditorHeight();
                      }, 0);
                    }}
                  />
                  {!isDictationOnGoing && (() => {
                    const isDisabled =
                      isSendDisabled ||
                      onProcessing.length > 0 ||
                      isSendBlocked;

                    if (isSendBlocked) {
                      return (
                        <StopGeneratingMessage
                          isLoading={!canCancelGeneration}
                          isCancelOnGoing={isCancelOnGoing}
                        />);
                    }
                    return (
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              disabled={isDisabled}
                              aria-disabled="false"
                              aria-label="Send message"
                              className="flex h-7 w-7 items-center justify-center rounded-full border-none bg-[#000] p-0 text-white hover:!bg-[#3D3D3D] disabled:opacity-40 dark:bg-[#3D3D3D] dark:hover:!bg-[#525252] min-h-7 min-w-7 shrink-0 2xl:h-8 2xl:w-8"
                              onClick={handleSubmit}
                            >
                              <ArrowUp
                                width={16}
                                height={16}
                                strokeWidth={1.75}
                                className="size-3 text-[#ffffff] 2xl:size-4"
                              />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {t('SEND_MESSAGE', { defaultValue: 'Send message' })}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
          {hasParticipants && (
            <TypingIndicator />
          )}

        </div>
        </div>

        {showQuickActions && !isCreateAgentMode && (
          <div
            className={
              showExploreIdeas || showWriteOrEditPrompts || showAgents
                ? 'relative mt-0 min-h-[38px] w-full'
                : 'relative mt-0 hidden min-h-[38px] w-full lg:block'
            }
          >
            {/* Quick action chips: desktop only (hidden on mobile + tablet) */}
            {showQuickActionButtons && (
              <ChatQuickActions buttons={quickActions} />
            )}

            {(showExploreIdeas || showWriteOrEditPrompts || showAgents) && (
              <div ref={expandedQuickPanelRef} className="absolute inset-x-0 top-0 z-10">
                {showExploreIdeas && (
                  <ExploreIdeasSection
                    onUploadPhoto={onFilesSelected}
                    onSelectIdea={handleSelectImageIdea}
                  />
                )}

                {showWriteOrEditPrompts && (
                  <WriteOrEditPromptList onSelectPrompt={handleSelectWritePrompt} />
                )}

                {showAgents && (
                  <AgentsSection />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ApplyPromptVariablesModal
        open={!!applyPromptState}
        onOpenChange={(open) => {
          if (!open) setApplyPromptState(null);
        }}
        prompt={applyPromptState?.prompt ?? null}
        onApply={handleApplyPromptFromModal}
      />

      <NewProjectModal
        isOpen={isNewProjectModalOpen}
        onClose={() => setIsNewProjectModalOpen(false)}
        newConversatioId={activeConversationId ?? undefined}
        callback={() => {
          if (activeConversationId) {
            removeConversationFromSidebarById(activeConversationId);
          }
        }}
      />
    </>
  );
};